// bildirimRol.js — bildirimden gelen otomatik rol geçişi.
//
// SORUN: Bir web push bildirimi, kullanıcının O ANDAKİ rolünün açamadığı bir sayfaya
// derin link veriyordu. Sayfanın isteği 403 dönüyor, FE çıkış yapıyor ve kullanıcı
// çalışan oturumundan atılıyordu. Kullanıcı isteği: bildirimin hedeflediği role
// SAHİPSE otomatik o role geçilsin ve sayfa açılsın.
//
// ÇÖZÜM: Bildirim payload'ında hedef roller taşınır (BE bildirim_merkezi.js), service
// worker bunu tıklama adresine `bts_rol` parametresi olarak ekler, burada okunur.
//
// 🔴 TASARIMIN ASIL RİSKİ MUTLU YOL DEĞİL, DÖNGÜLERDİR. Uygulanan kurallar
// (.agent/HANDOFF.md §6b R1-R12):
//   R1  403 yüzünden ASLA rol değiştirilmez — yalnızca bildirimin BEYAN ettiği rol için.
//       (403 -> geçiş -> 403 -> geçiş ... sahip olunan her rolde dönen bir döngüdür.)
//   R2  Bir bildirim tıklaması için EN FAZLA BİR geçiş. Parametre işlenmeye başlar
//       başlamaz URL'den silinir; yenilenen sayfa aynı geçişi bir daha denemez.
//   R3  Hedef rol = mevcut rol -> hiç geçiş yapılmaz, doğrudan sayfada kalınır.
//   R4  Kullanıcı beyan edilen rollerin hiçbirine sahip değil -> tek bir bilgi mesajı;
//       geçiş yok, çıkış yok, tekrar yok.
//   R5  roldegistir başarısız -> mesaj + mevcut sayfada kal. OTOMATİK TEKRAR YOK
//       (hata deterministik; tekrar denemek döngüdür).
//   R6  Dönen `yeni_sayfa` doğrulanmadan gidilmez; boş/anlamsızsa güvenli sayfaya.
//   R8  Geçişler sıralanır: hızlı iki tıklama iki roldegistir yarıştırmamalı, yoksa
//       son rol belirsiz kalır ve iki sekme de 403 almaya başlar.
//   R10 Oturum kapalıyken tıklanan bildirim: burada hiçbir şey yapılmaz. Parametre
//       URL'de kalır; PrivateRoute dönüş yolunu (sorgu dahil) kaydeder, giriş sonrası
//       kullanıcı aynı adrese döner ve akış O ZAMAN çalışır.
//   R12 `roller` boş olan tipler (alıcı bağışı ekleyen kişi) rol taşımaz -> geçiş yok.
//
// KURAL: kullanıcıya rol KISALTMASI (sy/mp/bp...) gösterilmez, yalnızca uzun ad.

import { apiRooter } from "../component/yeni/Istek";
import { isAuth, setLocalStorage } from "./auth";
import { toastCreater } from "./utils";
import {
  rolGecisiBasladi,
  rolGecisiBitti,
  rolGecisiSuruyorMu,
} from "./yetkiYonlendirme";

const PARAM_ADI = "bts_rol";
const GUVENLI_SAYFA = "home";

// R8 bayrağı (bir geçiş sürerken ikincisi başlatılmaz) `yetkiYonlendirme.js`'te
// duruyor — gerekçe orada: döngüsel import kaçınması.

/**
 * Adreste bekleyen bir rol geçişi var mı? Parametreyi TÜKETMEZ, yalnızca bakar.
 *
 * NEDEN VAR: React'te çocuk effect'leri ebeveynden ÖNCE çalışır. Geçişi bir effect
 * içinde yapmak yetmiyor — sayfa o an mount olmuş ve kendi verisini çoktan istemiş
 * oluyor, eski rolle 403 alıyor ve kullanıcı gereksiz bir "yetki yok" uyarısı görüyor
 * (canlı gözlem 2026-08-04). Bu yüzden PrivateRoute RENDER SIRASINDA bunu sorup,
 * geçiş bitene kadar sayfayı hiç render etmiyor. Sıra: rol değişir, sonra sayfa açılır.
 */
export const rolGecisiGerekliMi = () => {
  try {
    return new URLSearchParams(window.location.search).has(PARAM_ADI);
  } catch {
    return false;
  }
};

// NOT: `rolGecisiSuruyorMu` bu modülden DIŞA VERİLMEZ; tek kaynak
// `yetkiYonlendirme.js`. İki yerde tutulsa biri güncellenip diğeri unutulurdu.

/**
 * Parametreyi URL'den okur ve HEMEN siler (R2). Silme işi geri kalan her şeyden önce
 * yapılır: sonrasında hata da olsa, sayfa yenilense de aynı geçiş tekrar denenmez.
 * @returns {Array<{id:string, ad:string, uzun_ad:string}>|null}
 */
const rolleriAlVeTemizle = () => {
  try {
    const parametreler = new URLSearchParams(window.location.search);
    const ham = parametreler.get(PARAM_ADI);
    if (!ham) return null;

    parametreler.delete(PARAM_ADI);
    const sorgu = parametreler.toString();
    // replaceState: geçmişe yeni kayıt eklemez — kullanıcı "geri" dediğinde
    // parametreli adrese dönüp geçişi yeniden tetiklemesin.
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (sorgu ? `?${sorgu}` : "") + window.location.hash
    );

    const cozulen = JSON.parse(ham);
    if (!Array.isArray(cozulen)) return null;
    // Bozuk/eksik kayıtları at: `ad` olmadan roldegistir çağrılamaz.
    return cozulen.filter((r) => r && typeof r.ad === "string" && r.ad);
  } catch (err) {
    // Bozuk parametre bildirim tıklamasını bozmamalı; sayfa normal açılır.
    console.error("[bildirimRol] rol parametresi okunamadı:", err);
    return null;
  }
};

// "/projeId/ayarlar/16" -> "ayarlar". roldegistir tek segmentle karşılaştırıyor.
const mevcutSayfaSegmenti = () => {
  const parcalar = window.location.pathname.split("/").filter(Boolean);
  const indeks = parcalar.indexOf("projeId");
  return indeks >= 0 ? parcalar[indeks + 1] || "" : parcalar[0] || "";
};

/**
 * Bildirimden gelen rol geçişini uygular. Yapılacak bir şey yoksa sessizce çıkar;
 * çağıran tarafın koşul kontrol etmesi gerekmez.
 *
 * SÖZ (promise) DÖNER: çağıran, sayfayı render etmeden önce beklemek zorunda
 * (bkz. rolGecisiGerekliMi). Başarılı geçişte zaten adres değişir; söz yalnızca
 * "yapılacak bir şey kalmadı" anını bildirir.
 */
export const bildirimRolunuUygula = async () => {
  const hedefRoller = rolleriAlVeTemizle();
  if (!hedefRoller || !hedefRoller.length) return; // R12 ve normal gezinme

  // R10: oturum yok -> hiçbir şey yapma. (Parametre yukarıda silindi; ama oturum
  // kapalıyken bu fonksiyon PrivateRoute tarafından hiç çağrılmaz, o yol dönüş
  // yolu mekanizmasıyla korunur.)
  const kullanici = isAuth();
  if (!kullanici) return;

  if (rolGecisiSuruyorMu()) return; // R8

  // Kullanıcının rol listesi rol DOKÜMANLARINI tutar ({_id, ad, uzun_ad}).
  const sahipOlunan = Array.isArray(kullanici.roller) ? kullanici.roller : [];
  const sahipIdler = sahipOlunan.map((r) => String(r?._id));
  const sahipAdlar = sahipOlunan.map((r) => r?.ad);

  // Kullanıcının GERÇEKTEN sahip olduğu ilk hedef rol. Tipin ilk rolünü körlemesine
  // almıyoruz: [sy, mp] gibi bir bildirimde alıcı ikinci role sahip olabilir.
  const hedef = hedefRoller.find(
    (r) => sahipIdler.includes(String(r.id)) || sahipAdlar.includes(r.ad)
  );

  if (!hedef) {
    // R4: sahip değil -> tek mesaj, başka hiçbir şey. Kısaltma değil uzun ad gösterilir.
    const adlar = hedefRoller.map((r) => r.uzun_ad || r.ad).join(" / ");
    toastCreater(`Bu bildirim ${adlar} yetkisi içindir.`, "e");
    return;
  }

  // R3: zaten o roldeyiz -> geçiş yapmak anlamsız (ve döngüden başka bir şey üretmez).
  if (hedef.ad === kullanici.rol) return;

  rolGecisiBasladi();
  const mevcutSayfa = mevcutSayfaSegmenti();

  try {
    const res = await apiRooter("post", "/api/roldegistir", {
      yeni_rol: hedef.ad,
      sayfa: mevcutSayfa,
    });

    // R5: başarısızlık TERMİNAL. Hata mesajını handleApiError zaten gösterdi;
    // burada tekrar denemiyoruz, sayfada kalıyoruz.
    if (!res) {
      rolGecisiBitti();
      return;
    }

    // R6: BE bulunduğumuz sayfayı yeni rol için uygun görmediyse döndürdüğü sayfaya
    // git; uygun gördüyse (aynı segment) bildirimin KENDİ hedefinde kal — adresteki
    // sekme/alt yol bilgisi (ör. /ayarlar/16) böylece kaybolmaz.
    const yeniSayfa = res.yeni_sayfa || GUVENLI_SAYFA;

    setLocalStorage("user", {
      ...isAuth(),
      rol: hedef.ad,
      rol_ad: res.yeni_rol_ad || hedef.uzun_ad,
      // defaultPage YENİLENİR: girişte hesaplanmış değer artık ESKİ role ait ve
      // yetkisiz-yönlendirme (helpers/yetkiYonlendirme.js) onu güvenli sayfa olarak
      // kullanıyor. Güncellemezsek, rol değiştikten sonra 403 alan kullanıcı yeni
      // rolünün göremediği bir sayfaya atılırdı — düzeltmeye çalıştığımız şeyin aynısı.
      defaultPage: res.defaultPage || yeniSayfa, // 2026-10-08: BE'nin rol bazlı güvenli sayfası öncelikli
    });

    // Adres DEĞİŞMEDEN bayrak bırakılmaz: aşağıdaki iki dal da sayfayı baştan
    // yükler, yani bu modül zaten sıfırdan başlar.
    if (yeniSayfa !== mevcutSayfa) {
      window.location.assign(`/projeId/${yeniSayfa}`);
      return;
    }
    // Aynı sayfa: rol değişti, veriler yeni yetkiyle baştan çekilmeli.
    // Parametre silinmiş olduğu için yenileme aynı geçişi tetiklemez.
    window.location.reload();
  } catch (err) {
    // apiRooter normalde hatayı kendi yakalar; buraya beklenmeyen bir durumda düşülür.
    console.error("[bildirimRol] rol değiştirilemedi:", err);
    rolGecisiBitti();
  }
};
